import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable, map, of, switchMap, tap } from 'rxjs';
import { LookupStore } from '@core/lookup/lookup.store';
import { LOOKUP_CODES } from '@core/lookup/lookup-codes';
import { DependantResponse } from '@features/employee/employee.model';
import { EmployeeApiService } from '@features/employee/employee-api.service';
import { EmployeeStore } from '@features/employee/employee.store';
import { LookupSelectComponent } from '@shared/components/lookup-select/lookup-select.component';
import { SmsCodeConfirmComponent, SmsCodeSent } from '@shared/components/sms-code-confirm/sms-code-confirm.component';
import { YesNoToggleComponent } from '@shared/components/yes-no-toggle/yes-no-toggle.component';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { nullIfBlank } from '@shared/utils/payload-utils';
import { InterventionApiService } from '../../../intervention-api.service';
import { InterventionStore } from '../../../intervention.store';
import { EligibilityDependantRequest, MemberContextResponse } from '../../../intervention.model';

const STATUS_TEXT: Record<string, string> = { Pending: 'Not answered yet', Authorised: 'Authorised', NotAuthorised: 'Not authorised' };
const METHOD_TEXT: Record<string, string> = { Verbal: 'recorded by the clinician', EmailLink: 'by the email link', SmsCode: 'by SMS code' };

// How a dependant's authorisation reads: the status, and for an authorised one how it was given.
export function dependantStatusText(status: string | null | undefined, method: string | null | undefined): string | null {
  if (!status) {
    return null;
  }

  const text = STATUS_TEXT[status] ?? status;
  return status === 'Authorised' && method ? `${text} (${METHOD_TEXT[method] ?? method})` : text;
}

// The dependant the case is for (the Eligibility step), when the member is a dependent: an existing dependant of the employee or a new one, the
// main member's authorisation (Yes or No with a reason), and the SMS code that authorises. The case goes on whatever the member answers; the
// answer is recorded for reporting. A dependant added here stays on the employee's file.
@Component({
  selector: 'eligibility-dependant-section',
  imports: [YesNoToggleComponent, LookupSelectComponent, ReactiveFormsModule, SmsCodeConfirmComponent, ...SharedMaterialUIModule],
  templateUrl: './eligibility-dependant-section.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EligibilityDependantSectionComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly lookupStore = inject(LookupStore);
  private readonly employeeStore = inject(EmployeeStore);
  private readonly employeeApi = inject(EmployeeApiService);
  private readonly interventionApi = inject(InterventionApiService);
  protected readonly store = inject(InterventionStore);

  readonly interventionId = input.required<string>();
  readonly member = input.required<MemberContextResponse>();
  readonly readOnly = input(false);

  protected readonly editing = signal(false);

  protected readonly dependantTypes = computed(() => this.lookupStore.items(LOOKUP_CODES.dependantType));
  protected readonly genders = computed(() => this.lookupStore.items(LOOKUP_CODES.gender));
  protected readonly maritalStatuses = computed(() => this.lookupStore.items(LOOKUP_CODES.maritalStatus));
  protected readonly declineReasons = computed(() => this.lookupStore.items(LOOKUP_CODES.dependantDeclineReason));
  protected readonly existingDependants = computed(() => this.employeeStore.dependants());

  protected readonly form = this.formBuilder.group({
    mode: this.formBuilder.nonNullable.control<'existing' | 'new'>('new'),
    dependantId: [''],
    dependantTypeId: [''],
    firstName: [''],
    surname: [''],
    idNumber: [''],
    contactNumber: [''],
    emailAddress: ['', Validators.email],
    genderId: [''],
    maritalStatusId: [''],
    // The main member's answer: true, false, or null while it is not given.
    authorised: this.formBuilder.control<boolean | null>(null),
    declineReasonId: [''],
    declineReasonOther: [''],
  });

  // The current dependant's authorisation as it is held now.
  protected readonly currentStatus = computed(() => dependantStatusText(this.member().authorisationStatus, this.member().authorisationMethod));

  constructor() {
    this.lookupStore.load([LOOKUP_CODES.dependantType, LOOKUP_CODES.gender, LOOKUP_CODES.maritalStatus, LOOKUP_CODES.dependantDeclineReason]);

    // The employee's dependants, for the list of an existing one.
    effect(() => {
      const employeeId = this.member().employeeId;
      if (employeeId) {
        this.employeeStore.loadDependants(employeeId, true);
      }
    });

    // What each answer needs: a new dependant a name and a mobile number, an existing one a choice, and a refusal a reason.
    this.form.valueChanges.subscribe(() => this.applyRequirements());
    this.applyRequirements();
  }

  protected selectedExisting(): DependantResponse | null {
    const controls = this.form.controls;
    return controls.mode.value === 'existing' && controls.dependantId.value
      ? (this.existingDependants().find((item) => item.id === controls.dependantId.value) ?? null)
      : null;
  }

  protected existingStatusText(): string | null {
    const dependant = this.selectedExisting();
    return dependant ? dependantStatusText(dependant.authorisationStatus, dependant.authorisationMethod) : null;
  }

  protected isOtherReason(): boolean {
    return this.declineReasons().find((item) => item.id === this.form.controls.declineReasonId.value)?.code === 'OTHER';
  }

  protected dependantSelected(): void {
    const dependant = this.selectedExisting();
    if (dependant) {
      this.form.patchValue({ contactNumber: dependant.contactNumber ?? '', emailAddress: dependant.emailAddress ?? '' });
    }
  }

  protected clearAuthorisation(): void {
    this.form.patchValue({ authorised: null, declineReasonId: '', declineReasonOther: '' });
  }

  protected edit(): void {
    this.form.reset({ mode: this.existingDependants().length > 0 ? 'existing' : 'new', authorised: null });
    this.editing.set(true);
  }

  protected cancel(): void {
    this.editing.set(false);
  }

  private applyRequirements(): void {
    const controls = this.form.controls;
    const isNew = controls.mode.value === 'new';
    const refused = controls.authorised.value === false;

    this.setRequired(controls.firstName, isNew);
    this.setRequired(controls.surname, isNew);
    this.setRequired(controls.contactNumber, isNew);
    this.setRequired(controls.dependantId, !isNew);
    this.setRequired(controls.declineReasonId, refused);
    this.setRequired(controls.declineReasonOther, refused && this.isOtherReason());
  }

  private setRequired(control: AbstractControl, required: boolean): void {
    if (required) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  // What is sent for the dependant. One on file goes by its id (plus what was corrected); a new one by its details.
  private request(): EligibilityDependantRequest {
    const value = this.form.getRawValue();
    const existing = value.mode === 'existing' && !!value.dependantId;
    const refused = value.authorised === false;

    return {
      dependantId: existing ? value.dependantId : null,
      dependantTypeId: value.dependantTypeId || null,
      firstName: existing ? null : nullIfBlank(value.firstName),
      surname: existing ? null : nullIfBlank(value.surname),
      idNumber: existing ? null : nullIfBlank(value.idNumber),
      contactNumber: nullIfBlank(value.contactNumber),
      emailAddress: nullIfBlank(value.emailAddress),
      genderId: value.genderId || null,
      maritalStatusId: value.maritalStatusId || null,
      authorisedByMember: value.authorised,
      declineReasonId: refused ? value.declineReasonId || null : null,
      declineReasonOther: refused ? nullIfBlank(value.declineReasonOther) : null,
    };
  }

  protected save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.store.captureEligibilityDependant(this.interventionId(), this.request(), () => this.editing.set(false));
  }

  // A code can be sent once there is a dependant to send it for: one chosen, or a new one with a name, a surname and a mobile number.
  protected canSend(): boolean {
    if (this.readOnly()) {
      return false;
    }

    const controls = this.form.controls;
    const filled = (value: string | null) => !!(value ?? '').trim();
    return controls.mode.value === 'existing'
      ? filled(controls.dependantId.value)
      : filled(controls.firstName.value) && filled(controls.surname.value) && filled(controls.contactNumber.value);
  }

  // Sends the member a code. A dependant that is not on the employee's file yet is captured first (which makes the case theirs), so there is
  // someone to authorise.
  protected readonly sendCode = (): Observable<SmsCodeSent> =>
    this.ensureOnFile().pipe(
      switchMap((dependantId) =>
        this.employeeApi.sendDependantAuthorisationCode(this.member().employeeId!, dependantId, this.member().memberMobileNumber || null),
      ),
    );

  // A right code authorises the dependant: the step is read again so it shows the status.
  protected readonly verifyCode = (code: string): Observable<DependantResponse> =>
    this.employeeApi.verifyDependantAuthorisationCode(this.member().employeeId!, this.form.controls.dependantId.value ?? '', code).pipe(
      tap(() => {
        this.form.patchValue({ authorised: true, declineReasonId: '', declineReasonOther: '' });
        this.store.loadEligibility(this.interventionId());
      }),
    );

  private ensureOnFile(): Observable<string> {
    const controls = this.form.controls;
    const existingId = controls.mode.value === 'existing' ? controls.dependantId.value : null;
    if (existingId) {
      return of(existingId);
    }

    return this.interventionApi.captureEligibilityDependant(this.interventionId(), this.request()).pipe(
      tap((eligibility) => {
        this.store.eligibility.set(eligibility);
        if (eligibility.member.employeeId) {
          this.employeeStore.loadDependants(eligibility.member.employeeId, true);
        }

        // From here on the dependant is one on the file, so saving does not add it a second time.
        this.form.patchValue({ mode: 'existing', dependantId: eligibility.member.dependantId ?? '' });
      }),
      map((eligibility) => eligibility.member.dependantId ?? ''),
    );
  }
}
